import { Col, Row } from 'react-bootstrap';
import StepTabs from '../../../components/StepTabs';
import PropTypes from 'prop-types';
import { useMyContentContext } from '../utils/myContentContext';
import { useSaveCRQuestionMutation, 
  useSaveMcFillInQuestionMutation,
  useUpdateCRQuestionMutation,
  useUpdateQuestionMutation } from '../../../features/authentication/qSetApiSlice';
import QuestionTypes from '../../../enums/questionTypes';
import CustomToast from '../../../components/Toast';
import CreateQuestionSetModel from '../pages/questionSets/components/CreateQuestionSetModel';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { bytesToBase64 } from '../../../utils/pdfUtils';

const QuestionEditorHeader = ({
  activeStep,
  setActiveStep,
  setpOnClick,
  isCRQuestion
}) => {
  const {
    question,
    hint,
    reason,
    questionType,
    fillInAnswer,
    fillInAnswerBoxWidth,
    fillInAnswerBoxFontSize,
    correctAnswer,
    numChoices,
    selectedAttributeNodes,
    selectedTagNodes,
    selectedStandardNodes,
    selectedTopicNodes,
    questionDocuments,
    answerSize,
    sampleAnswer,
    editAnswer,
    maxPoints,
    options
  } = useMyContentContext();

  const steps = isCRQuestion ?
    [
      { label: 'Question' },
      { label: 'Sample Answer' },
      { label: 'Options' },
      { label: 'Topics/Tags/<br/>Attributes/Standards' }
    ] :
    [
      { label: 'Question' },
      { label: 'Hint' },
      { label: 'Reason' },
      { label: 'Answer Options' },
      { label: 'Topics/Tags/<br/>Attributes/Standards' }
    ];

  const { setId, questionId } = useParams();
  const [saveMcFillInQuestion] = useSaveMcFillInQuestionMutation();
  const [saveCRQuestion] = useSaveCRQuestionMutation();
  const [updateTQuestion] = useUpdateQuestionMutation();
  const [updateTCRQuestion] = useUpdateCRQuestionMutation();
  const [openCreateQuestion, setOpenCreateQuestion] = useState(false);
  const navigate = useNavigate();

  const handleMcFillInSave = async (setId) => {
    const error = validateMcFillInQuestion();
    if (error.length > 0) {
      CustomToast(error, 'error');
      return;
    }
    if (!setId && activeStep == 5) {
      setOpenCreateQuestion(true);
    }
    else if (activeStep >= 5) {
      if (questionId) {
        await updateMcFillInQuestionAsync();
        navigate(`/mycontent/questionsets/${setId}/questions`);
      }
      else {
        await saveMcFillInQuestionAsync(setId);
        window.history.back();
      }
    }
  };

  const updateMcFillInQuestionAsync = async () => {
    let option = {
      id: questionId,
      setId: setId,
      question:bytesToBase64(new TextEncoder().encode(question)),
      hint: bytesToBase64(new TextEncoder().encode(hint)),
      reason: bytesToBase64(new TextEncoder().encode(reason)),
      numChoices: numChoices,
      mcAnswer: correctAnswer,
      fillInAnswer: fillInAnswer,
      fillInAnswerFontSize: fillInAnswerBoxFontSize,
      fillInAnswerWidth: fillInAnswerBoxWidth,
      qType: questionType,
      properties: {
        tags: selectedTagNodes,
        topics: toValueNameModel(selectedTopicNodes),
        attributes: selectedAttributeNodes,
        standards: selectedStandardNodes
      },
      documents: questionDocuments.map(obj => obj.documentId)
    };
    const response = await updateTQuestion(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      CustomToast('Question updated successfully', 'success');
    }
  };

  const saveMcFillInQuestionAsync = async (setId) => {
    let option = {
      id: setId,
      params: {
        question: bytesToBase64(new TextEncoder().encode(question)),
        hint: bytesToBase64(new TextEncoder().encode(hint)),
        reason: bytesToBase64(new TextEncoder().encode(reason)),
        numChoices: numChoices,
        mcAnswer: correctAnswer,
        fillInAnswer: fillInAnswer,
        fillInAnswerFontSize: fillInAnswerBoxFontSize,
        fillInAnswerWidth: fillInAnswerBoxWidth,
        qType: questionType,
        properties: {
          tags: selectedTagNodes,
          topics: toValueNameModel(selectedTopicNodes),
          attributes: selectedAttributeNodes,
          standards: selectedStandardNodes
        },
        documents: questionDocuments.map(obj => obj.documentId)
      }
    };
    const response = await saveMcFillInQuestion(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      CustomToast('Question added successfully', 'success');
    }
  };

  const validateMcFillInQuestion = () => {
    const cleanQ = question.replace(/&nbsp;/g, '').replace(/ /g, '');
    const emptyQuestion = !cleanQ || cleanQ.trim() === '' || cleanQ.indexOf('<p></p>') === 0;
    if (emptyQuestion) {
      return 'The question field is cannot be blank.';
    }
    if (questionType === QuestionTypes.FillIn) {
      let count = 0;
      let offset = 0;
      let questionString = question;
      while ((offset = (' ' + questionString).indexOf('~~1~~', offset + 1)) !== -1) {
        count++;
        if (count > 1) break;
      }
      if (count < 1) {
        questionString += '<p>Answer: ~~1~~</p>';
        return 'No answer field was specified, so one has been appended to the end of the question. An answer field is specified by including ~~1~~ in the question.';
      } else if (count > 1) {
        return 'You can only specify one answer field. Please remove additional ~~1~~ strings from the question.';
      }
      if (fillInAnswer.length == 0 && activeStep > 3) {
        return 'The fill-in answer cannot be blank.';
      }
    }
    else if (questionType == QuestionTypes.MC) {
      const quesLower = (question || '').toLowerCase();
      if (!(quesLower.includes('<ol') && quesLower.includes('<li'))) {
        return 'No multiple-choice answers were created inside the question text. Please create answer choices by using the tool.';
      }

      let count = 0;
      let offset = 0;
      while ((offset = (' ' + quesLower).indexOf('<li', offset + 1)) !== -1) {
        count++;
      }

      if (count !== numChoices && activeStep > 3) {
        return 'The number of answer choices in the question text does not equal the number specified under \'Number of Choices:\' <br/> Please try again.';
      }

      if ((correctAnswer > numChoices) && activeStep > 3) {
        return 'The multiple choice answer cannot be a larger number than the number of choices.';
      }
    }
    if (activeStep <= 4)
      setActiveStep(activeStep + 1);
    return '';
  };

  const handleCRSave = async (setId) => {
    const error = validateCRQuestion();
    if (error.length > 0) {
      CustomToast(error, 'error');
      return;
    }
    if (!setId && activeStep == 4) {
      setOpenCreateQuestion(true);
    }
    else if (activeStep >= 4) {
      if (questionId) {
        await updateCRQuestionAsync();
        navigate(`/mycontent/questionsets/${setId}/questions`);
      }
      else {
        await saveCRQuestionAsync(setId);
        window.history.back();
      }
    }
  };

  const validateCRQuestion=()=>{
    const cleanQ = question.replace(/&nbsp;/g, '').replace(/ /g, '');
    const emptyQuestion = !cleanQ || cleanQ.trim() === '' || cleanQ.indexOf('<p></p>') === 0;
    if (emptyQuestion) {
      return 'The question field is cannot be blank.';
    }
    if (activeStep <= 3)
      setActiveStep(activeStep + 1);
    return '';
  };

  const saveCRQuestionAsync = async (setId) => {
    let option = {
      id: setId,
      params: {
        question: bytesToBase64(new TextEncoder().encode(question)),
        answer: bytesToBase64(new TextEncoder().encode(sampleAnswer)),
        answerSize: answerSize,
        maxPoints: maxPoints,
        editAnswer: editAnswer,
        options: options,
        properties: {
          tags: selectedTagNodes,
          topics: toValueNameModel(selectedTopicNodes),
          attributes: selectedAttributeNodes,
          standards: selectedStandardNodes
        },
        documents: questionDocuments.map(obj => obj.documentId)
      }
    };
    const response = await saveCRQuestion(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Description, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      CustomToast('Question added successfully', 'success');
    }
  };

  const updateCRQuestionAsync = async () => {
    let option = {
      id: questionId,
      setId: setId,
      question: bytesToBase64(new TextEncoder().encode(question)),
      answer: bytesToBase64(new TextEncoder().encode(sampleAnswer)),
      answerSize: answerSize,
      editAnswer:editAnswer,
      maxPoints:maxPoints,
      options:options,
      properties: {
        tags: selectedTagNodes,
        topics: toValueNameModel(selectedTopicNodes),
        attributes: selectedAttributeNodes,
        standards: selectedStandardNodes
      },
      documents: questionDocuments.map(obj => obj.documentId)
    };
    const response = await updateTCRQuestion(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      CustomToast('Question updated successfully', 'success');
    }
  };

  const toValueNameModel = (topics)=>{
    const topicArray = topics.map(({ id, name }) => ({
      value: id,
      name
    }));
    return topicArray;
  };

  return (
    <>
      <Row className='align-items-center d-flex justify-content-between p-4 pb-5'>
        <Col className='col-2'><h1 className=''>Question Editor</h1></Col>
        <Col className='col-5 mb-4'>
          <StepTabs
            steps={steps}
            activeStep={activeStep}
            onClick={setpOnClick} />
        </Col>
        {((isCRQuestion && activeStep == 5) || (isCRQuestion && activeStep == 4)) ? (
          <Col className='d-flex justify-content-end col-3 gap-3'>
            <button id="back_button" className="btn btn-castleWhite btn-width" onClick={() => window.history.back()}>
              Cancel
            </button>
            <button id="save_button" className="btn btn-castleRed btn-width" onClick={() => isCRQuestion ? handleCRSave(setId) : handleMcFillInSave(setId)}>
              Save
            </button>
          </Col>
        ) : (
          <Col className='d-flex justify-content-end col-2'>
            <button id="save_button" className="btn btn-castleRed" onClick={() => isCRQuestion ? handleCRSave(setId) : handleMcFillInSave(setId)}>
              Save and Continue
            </button>
          </Col>
        )}
      </Row>
      <CreateQuestionSetModel
        isOpen={openCreateQuestion}
        onClose={() => setOpenCreateQuestion(false)}
        showQuestionOptions={false}
        onCreateSuccess={isCRQuestion ? handleCRSave : handleMcFillInSave}
      />
    </>
  );
};

QuestionEditorHeader.propTypes = {
  activeStep: PropTypes.number,
  setActiveStep: PropTypes.func,
  setpOnClick: PropTypes.func,
  isCRQuestion: PropTypes.bool
};

export default QuestionEditorHeader;